<template>
  <v-main>
    <v-container fluid class="dashboard-container">
      <!-- Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <div class="header-content hero">
            <div>
              <h1 class="dashboard-title">{{ getGreeting() }}</h1>
              <p class="dashboard-subtitle">{{ getCurrentDate() }}</p>
            </div>
            <div class="header-actions">
              <dashboard-date-filter v-model="dashboardDateFilter" @change="onDashboardDateChange" class="mr-2 hero-filter" />
              <v-btn color="white" outlined small @click="refreshDashboard" :loading="isLoading" class="mr-2">
                <v-icon left small>mdi-refresh</v-icon> Refresh
              </v-btn>
              <v-chip small :color="websocketStatus.color" text-color="white">
                <v-icon left x-small>{{ websocketStatus.icon }}</v-icon>
                {{ websocketStatus.text }}
              </v-chip>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Quick Stats Cards - Row 1 -->
      <v-row dense>
        <v-col cols="6" sm="6" md="3" v-for="(stat, index) in quickStats" :key="'s1-' + index">
          <v-card class="stat-card" :class="'stat-' + stat.color" elevation="1" @click="goToKpi(stat)">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <div class="stat-icon-wrap mr-3" :class="'bg-' + stat.color">
                  <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="stat-value">{{ stat.value }}</div>
                  <div class="stat-label">{{ stat.label }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Quick Stats Cards - Row 2 -->
      <v-row dense class="mt-1">
        <v-col cols="6" sm="6" md="3" v-for="(stat, index) in quickStats2" :key="'s2-' + index">
          <v-card class="stat-card" :class="'stat-' + stat.color" elevation="1" @click="goToKpi(stat)">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <div class="stat-icon-wrap mr-3" :class="'bg-' + stat.color">
                  <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="stat-value">{{ stat.value }}</div>
                  <div class="stat-label">{{ stat.label }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Dashboard Content -->
      <v-row class="mt-2">
        <!-- Left Column -->
        <v-col cols="12" md="8">
          <!-- Revenue Chart -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="primary" small>mdi-chart-line</v-icon>
              Revenue Analytics
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="revenuePeriod" mandatory dense class="toggle-sm">
                <v-btn x-small value="6months">6 Months</v-btn>
                <v-btn x-small value="year">Year</v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-card-text class="pt-0">
              <canvas ref="revenueChart" height="90"></canvas>
            </v-card-text>
          </v-card>

          <!-- Orders Overview -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="deep-orange" small>mdi-cart</v-icon>
              Orders Overview
            </v-card-title>
            <v-card-text class="pt-0">
              <!-- Order Status Cards Row -->
              <div class="overview-cards-row mb-3">
                <div class="overview-card" v-for="(o, i) in orderSummary" :key="i">
                  <div class="overview-card-dot" :style="{ backgroundColor: o.color }"></div>
                  <div class="overview-card-count">{{ o.count }}</div>
                  <div class="overview-card-label">{{ o.label }}</div>
                </div>
              </div>
              <!-- Chart, or a friendly message when there is nothing to chart -->
              <health-loader v-if="isLoading" size="md" message="Loading orders..." />
              <empty-state
                v-else-if="!hasOrderData"
                icon="mdi-cart-outline"
                title="No orders in this period"
                message="Orders will show up here once customers place them. Try a wider date range to see earlier orders."
              />
              <div class="chart-centered" v-show="hasOrderData && !isLoading">
                <canvas ref="ordersChart"></canvas>
              </div>
            </v-card-text>
          </v-card>

          <!-- Appointments Overview -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="blue" small>mdi-calendar-clock</v-icon>
              Appointments Overview
            </v-card-title>
            <v-card-text class="pt-0">
              <!-- Appointment Status Cards Row -->
              <div class="overview-cards-row mb-3">
                <div class="overview-card" v-for="(a, i) in appointmentSummary" :key="i">
                  <div class="overview-card-dot" :class="a.statusClass"></div>
                  <div class="overview-card-count">{{ a.count }}</div>
                  <div class="overview-card-label">{{ a.label }}</div>
                </div>
              </div>
              <!-- Chart, or a friendly message when there is nothing to chart -->
              <health-loader v-if="isLoading" size="md" message="Loading appointments..." />
              <empty-state
                v-else-if="!hasAppointmentData"
                icon="mdi-calendar-blank-outline"
                title="No appointments in this period"
                message="Booked consultations will appear here. Try a wider date range to see earlier appointments."
              />
              <div class="chart-centered" v-show="hasAppointmentData && !isLoading">
                <canvas ref="appointmentsChart"></canvas>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right Column -->
        <v-col cols="12" md="4">
          <!-- Top Products -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="green" small>mdi-package-variant</v-icon>
              Top Products
            </v-card-title>
            <v-card-text class="pt-0">
              <div v-if="topProducts.length && topProducts[0].name !== 'Loading...'" >
                <div class="top-item" v-for="(p, i) in topProducts" :key="i">
                  <span class="top-rank">{{ i + 1 }}</span>
                  <span class="top-name">{{ p.name }}</span>
                  <span class="top-meta">{{ p.sales }} sold</span>
                  <v-chip x-small color="success" dark class="ml-auto">₹{{ p.revenue }}</v-chip>
                </div>
              </div>
              <div v-else class="text-center py-6 grey--text text--lighten-1">
                <v-icon large color="grey lighten-2" class="mb-2">mdi-package-variant-closed</v-icon>
                <div class="text-body-2">No product data yet</div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Top Sellers -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="indigo" small>mdi-store</v-icon>
              Top Sellers
            </v-card-title>
            <v-card-text class="pt-0">
              <div v-if="topSellers.length && topSellers[0].name !== 'Loading...'">
                <div class="top-item" v-for="(s, i) in topSellers" :key="i">
                  <v-avatar size="28" :color="['indigo','blue','teal','cyan','purple'][i] || 'grey'" class="mr-2">
                    <span class="white--text text-caption font-weight-bold">{{ s.initials }}</span>
                  </v-avatar>
                  <span class="top-name">{{ s.name }}</span>
                  <span class="top-meta">{{ s.orders }} orders</span>
                  <v-chip x-small color="indigo" dark class="ml-auto">₹{{ s.revenue }}</v-chip>
                </div>
              </div>
              <div v-else class="text-center py-6 grey--text text--lighten-1">
                <v-icon large color="grey lighten-2" class="mb-2">mdi-store-off</v-icon>
                <div class="text-body-2">No seller data yet</div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Pending Approvals -->
          <v-card class="mb-3 dash-card" elevation="1" ref="pendingApprovalsCard">
            <v-card-title class="dash-card-title">
              <v-icon left color="red" small>mdi-alert-circle</v-icon>
              Pending Approvals
              <v-spacer></v-spacer>
              <v-chip x-small color="red" dark>{{ quickStats2[3].value }}</v-chip>
            </v-card-title>
            <v-card-text class="pt-0">
              <div v-if="pendingApprovals.length > 0">
                <div class="approval-item" v-for="(item, index) in pendingApprovals" :key="index" @click="navigateTo(item.route)">
                  <v-icon small :color="item.color" class="mr-2">{{ item.icon }}</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium">{{ item.text }}</div>
                    <div class="text-caption grey--text">{{ item.count }} pending</div>
                  </div>
                  <v-btn x-small outlined color="primary">Review</v-btn>
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                <v-icon color="green">mdi-check-circle</v-icon>
                <div class="text-body-2 mt-1">No pending approvals</div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Recent Activities -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="purple" small>mdi-clock-outline</v-icon>
              Recent Activities
            </v-card-title>
            <v-card-text class="pt-0">
              <div v-if="recentActivities.length" class="activity-list">
                <div class="activity-row" v-for="(activity, index) in recentActivities" :key="index">
                  <v-icon small :color="activity.color" class="activity-icon">{{ activity.icon }}</v-icon>
                  <div class="activity-info">
                    <div class="text-body-2">{{ activity.text }}</div>
                    <div class="text-caption grey--text">{{ activity.time }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                <v-icon color="grey lighten-1">mdi-timeline-outline</v-icon>
                <div class="text-body-2 mt-1">No recent activity yet</div>
              </div>
            </v-card-text>
          </v-card>

        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>
<script>
import Chart from 'chart.js/auto';
import axios from 'axios';
import WebSocketService from '@/services/websocket';
import DashboardDateFilter from '@/components/DashboardDateFilter.vue';

export default {
  name: "HomePage",
  components: { DashboardDateFilter },
  data() {
    return {
      isLoading: false,
      dashboardDateFilter: { preset: 'this_month', start_date: null, end_date: null },
      revenuePeriod: '6months',
      dashboardData: {},
      quickStats: [
        { icon: 'mdi-currency-inr', value: '0', label: 'Total Revenue', color: 'green', valueType: 'currency',
          path: '/orders', query: { status: 'delivered' } },
        { icon: 'mdi-cart', value: '0', label: 'Total Orders', color: 'blue', valueType: 'number',
          path: '/orders', query: {} },
        { icon: 'mdi-package-variant', value: '0', label: 'Products Sold', color: 'orange', valueType: 'number',
          path: '/products', query: { sold: 1 } },
        { icon: 'mdi-account-multiple', value: '0', label: 'Total Patients', color: 'purple', valueType: 'number',
          path: '/patients', query: {} },
      ],
      quickStats2: [
        { icon: 'mdi-store', value: '0', label: 'Active Sellers', color: 'teal',
          path: '/sellers', query: { is_active: 1, registration_completed: 1 } },
        { icon: 'mdi-doctor', value: '0', label: 'Active Doctors', color: 'indigo',
          path: '/doctors', query: { is_active: 1, registration_completed: 1 } },
        { icon: 'mdi-calendar-check', value: '0', label: "Today's Appointments", color: 'cyan',
          path: '/appointments', query: {}, dynamicToday: true },
        { icon: 'mdi-alert-circle-outline', value: '0', label: 'Pending Approvals', color: 'red',
          scrollTarget: true },
      ],
      orderSummary: [
        { count: '0', label: 'New Orders', color: '#2196F3' },
        { count: '0', label: 'Processing', color: '#FF9800' },
        { count: '0', label: 'Shipped', color: '#9C27B0' },
        { count: '0', label: 'Delivered', color: '#4CAF50' },
        { count: '0', label: 'Cancelled', color: '#F44336' },
        { count: '0', label: 'Refunded', color: '#607D8B' },
      ],
      appointmentSummary: [
        { count: '0', label: 'Scheduled', statusClass: 'dot-blue' },
        { count: '0', label: 'Completed', statusClass: 'dot-green' },
        { count: '0', label: 'Cancelled', statusClass: 'dot-red' },
        { count: '0', label: 'In Progress', statusClass: 'dot-orange' }
      ],
      topProducts: [
        { name: 'Loading...', sales: 0, revenue: '0', image: '' }
      ],
      topSellers: [
        { name: 'Loading...', initials: '--', orders: 0, revenue: '0' }
      ],
      quickActions: [
        { label: 'New Order', icon: 'mdi-cart-plus', color: 'primary', route: '/orders/create' },
        { label: 'Add Product', icon: 'mdi-plus-circle', color: 'success', route: '/products/create' },
        { label: 'New Patient', icon: 'mdi-account-plus', color: 'warning', route: '/patient/create' },
        { label: 'View Orders', icon: 'mdi-format-list-bulleted', color: 'info', route: '/orders' },
        { label: 'Sellers', icon: 'mdi-store', color: 'secondary', route: '/sellers' },
        { label: 'Approvals', icon: 'mdi-check-decagram', color: 'error', route: '#pending-approvals' },
      ],
      pendingApprovals: [],
      recentActivities: [],
      charts: { revenue: null, appointments: null, orders: null },
      websocketStatus: { text: 'Connecting...', color: 'warning', icon: 'mdi-wifi' }
    };
  },
  computed: {
    user() { return this.$store.getters['auth/user']; },
    hasOrderData() { return this.orderSummary.some(o => Number(o.count) > 0); },
    hasAppointmentData() { return this.appointmentSummary.some(a => Number(a.count) > 0); },
  },
  mounted() {
    this.initializeDashboard();
    this.connectWebSocket();
  },
  beforeDestroy() {
    // Stop background work when leaving the dashboard — otherwise polling keeps
    // hitting the API on every other page.
    this.stopPollingUpdates();
    if (this.wsCheckTimer) { clearTimeout(this.wsCheckTimer); this.wsCheckTimer = null; }
    Object.values(this.charts).forEach(c => { if (c) c.destroy(); });
    this.disconnectWebSocket();
  },
  methods: {
    onDashboardDateChange(range) {
      this.fetchOrderStats(range);
      this.fetchProductStats(range);
      this.fetchAppointmentStats(range);
    },

    async initializeDashboard() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.fetchDashboardData(),
          this.loadCharts(),
        ]);
        await Promise.all([
          this.fetchOrderStats(this.dashboardDateFilter),
          this.fetchProductStats(this.dashboardDateFilter),
          this.fetchAppointmentStats(this.dashboardDateFilter),
        ]);
      } catch (e) { console.error('Dashboard init error:', e); }
      finally { this.isLoading = false; }
    },

    async fetchDashboardData() {
      try {
        // Only endpoints whose data is rendered on the dashboard (categories / payouts / reviews / wishlists stats were fetched but never displayed)
        const endpoints = ['/users/admin_dashboard', '/users/admin_dashboard/recent_activity', '/consultation_billings/summary'];
        const responses = await Promise.allSettled(endpoints.map(ep => axios.get(ep)));
        responses.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value?.data) this.processDashboardData(endpoints[i], r.value.data);
        });
      } catch (e) { console.error('Fetch error:', e); }
    },

    processDashboardData(endpoint, payload) {
      switch (endpoint) {
        case '/users/admin_dashboard': this.updateQuickStats(payload.data || payload); break;
        case '/users/admin_dashboard/recent_activity': this.recentActivities = payload.data || []; break;
        case '/consultation_billings/summary': this.updateRevenueData(payload); break;
        default: this.dashboardData[endpoint] = payload;
      }
    },

    updateQuickStats(data) {
      if (data.monthly_revenue !== undefined) this.quickStats[0].value = '₹' + this.formatNumber(data.monthly_revenue);
      if (data.total_orders !== undefined) this.quickStats[1].value = data.total_orders.toString();
      if (data.products_sold !== undefined) this.quickStats[2].value = data.products_sold.toString();
      if (data.total_patients !== undefined) this.quickStats[3].value = data.total_patients.toString();
      if (data.active_sellers !== undefined) this.quickStats2[0].value = data.active_sellers.toString();
      if (data.active_doctors !== undefined) this.quickStats2[1].value = data.active_doctors.toString();
      if (data.todays_appointments !== undefined) this.quickStats2[2].value = data.todays_appointments.toString();
      if (data.pending_approvals !== undefined) this.quickStats2[3].value = data.pending_approvals.toString();
      if (data.pending_breakdown) this.buildPendingApprovalsList(data.pending_breakdown);
    },

    buildPendingApprovalsList(breakdown) {
      const defs = [
        { key: 'doctors', text: 'Doctor registration pending', icon: 'mdi-doctor', color: 'blue',
          route: { path: '/doctors', query: { is_active: 1, registration_completed: 0 } } },
        { key: 'education', text: 'Education details pending', icon: 'mdi-school', color: 'orange',
          route: { path: '/education_details/approvals' } },
        { key: 'certificates', text: 'Council certificates pending', icon: 'mdi-certificate', color: 'purple',
          route: { path: '/council_registration_certificates/approval' } },
        { key: 'bank_details', text: 'Bank details pending', icon: 'mdi-bank', color: 'teal',
          route: { path: '/bank-details/approvals' } },
        { key: 'documents', text: 'Documents pending', icon: 'mdi-file-document', color: 'brown',
          route: { path: '/doctors' } },
        { key: 'pathy', text: 'Pathy verification pending', icon: 'mdi-shield-check', color: 'indigo',
          route: { path: '/doctors', query: { is_active: 1, is_verified_pathy: 0 } } },
      ];
      this.pendingApprovals = defs
        .map(d => ({ ...d, count: breakdown[d.key] || 0 }))
        .filter(d => d.count > 0)
        .sort((a, b) => b.count - a.count);
    },

    updateProductData(data) {
      if (Array.isArray(data.top_products)) {
        this.topProducts = data.top_products.map(p => ({
          name: p.name || 'Unknown', sales: p.sales || 0,
          revenue: this.formatNumber(p.revenue || 0), image: p.image || ''
        }));
      }
      if (Array.isArray(data.top_sellers)) {
        this.topSellers = data.top_sellers.map(s => ({
          name: s.name || 'Unknown', initials: (s.name || 'U').substring(0, 2).toUpperCase(),
          orders: s.orders || 0, revenue: this.formatNumber(s.revenue || 0),
        }));
      }
    },

    updateRevenueData(data) {
      if (this.charts.revenue && data.monthly_data) {
        this.charts.revenue.data.datasets[0].data = data.monthly_data;
        this.charts.revenue.update();
      }
    },

    async fetchOrderStats(range) {
      try {
        const params = range && range.preset === 'all'
          ? { period: 'all' }
          : { start_date: range?.start_date, end_date: range?.end_date };
        const { data } = await axios.get('/orders/stats', { params });
        if (data.success && data.data) {
          const d = data.data;
          this.orderSummary[0].count = d.pending.toString();
          this.orderSummary[1].count = d.processing.toString();
          this.orderSummary[2].count = d.shipped.toString();
          this.orderSummary[3].count = d.delivered.toString();
          this.orderSummary[4].count = d.cancelled.toString();
          this.orderSummary[5].count = d.refunded.toString();
          if (this.charts.orders) {
            this.charts.orders.data.datasets[0].data = [d.pending, d.processing, d.shipped, d.delivered, d.cancelled, d.refunded];
            this.charts.orders.update();
          }
        }
      } catch (e) { console.error('Order stats error:', e); }
    },

    async fetchProductStats(range) {
      try {
        const params = range && range.preset === 'all'
          ? { period: 'all' }
          : { start_date: range?.start_date, end_date: range?.end_date };
        const { data } = await axios.get('/products/stats', { params });
        if (data.success && data.data) this.updateProductData(data.data);
      } catch (e) { console.error('Product stats error:', e); }
    },

    async fetchAppointmentStats(range) {
      try {
        const params = range && range.preset === 'all'
          ? { period: 'all' }
          : { start_date: range?.start_date, end_date: range?.end_date };
        const { data } = await axios.get('/appointments/stats', { params });
        if (data.success && data.data) {
          const d = data.data;
          this.appointmentSummary[0].count = d.scheduled.toString();
          this.appointmentSummary[1].count = d.completed.toString();
          this.appointmentSummary[2].count = d.cancelled.toString();
          this.appointmentSummary[3].count = d.in_progress.toString();
          if (this.charts.appointments) {
            this.charts.appointments.data.datasets[0].data = [d.scheduled, d.completed, d.cancelled, d.in_progress];
            this.charts.appointments.update();
          }
        }
      } catch (e) { console.error('Appointment stats error:', e); }
    },

    loadCharts() {
      this.$nextTick(() => { this.initRevenueChart(); this.initOrdersChart(); this.initAppointmentsChart(); });
    },

    initRevenueChart() {
      if (!this.$refs.revenueChart) return;
      this.charts.revenue = new Chart(this.$refs.revenueChart.getContext('2d'), {
        type: 'line',
        data: {
          labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
          datasets: [
            { label: 'Revenue', data: [12000, 19000, 15000, 25000, 22000, 30000], borderColor: '#1976D2', backgroundColor: 'rgba(25,118,210,0.1)', tension: 0.4, fill: true },
            { label: 'Orders Value', data: [8000, 14000, 11000, 20000, 17000, 24000], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.05)', tension: 0.4, fill: true, borderDash: [5,5] }
          ]
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } } }, scales: { y: { beginAtZero: true, ticks: { callback: v => '₹' + v.toLocaleString(), font: { size: 10 } } }, x: { ticks: { font: { size: 10 } } } } }
      });
    },

    initOrdersChart() {
      if (!this.$refs.ordersChart) return;
      this.charts.orders = new Chart(this.$refs.ordersChart.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['New', 'Processing', 'Shipped', 'Delivered', 'Cancelled', 'Refunded'],
          datasets: [{ data: [0, 0, 0, 0, 0, 0], backgroundColor: ['#2196F3', '#FF9800', '#9C27B0', '#4CAF50', '#F44336', '#607D8B'] }]
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
      });
    },

    initAppointmentsChart() {
      if (!this.$refs.appointmentsChart) return;
      this.charts.appointments = new Chart(this.$refs.appointmentsChart.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['Scheduled', 'Completed', 'Cancelled', 'In Progress'],
          datasets: [{ data: [0, 0, 0, 0], backgroundColor: ['#2196F3', '#4CAF50', '#F44336', '#FF9800'] }]
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
      });
    },

    getGreeting() {
      const hour = new Date().getHours();
      const name = this.user?.first_name || 'User';
      if (hour < 12) return `Good Morning, ${name}!`;
      if (hour < 17) return `Good Afternoon, ${name}!`;
      return `Good Evening, ${name}!`;
    },

    getCurrentDate() {
      return new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    },

    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toString();
    },

    navigateTo(route) {
      if (route === '#pending-approvals') { this.scrollToPendingApprovals(); return; }
      this.$router.push(route).catch(() => {});
    },

    goToKpi(stat) {
      if (stat.scrollTarget) { this.scrollToPendingApprovals(); return; }
      const query = { ...(stat.query || {}) };
      if (stat.dynamicToday) {
        const today = this.getTodayIST();
        query.date_from = today;
        query.date_to = today;
      }
      this.$router.push({ path: stat.path, query }).catch(() => {});
    },

    getTodayIST() {
      return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    },

    scrollToPendingApprovals() {
      const el = this.$refs.pendingApprovalsCard?.$el || this.$refs.pendingApprovalsCard;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },

    connectWebSocket() {
      try {
        if (WebSocketService.isConnected()) {
          this.websocketStatus = { text: 'Live Updates', color: 'success', icon: 'mdi-wifi' };
          this.setupDashboardListeners();
          return;
        }
        WebSocketService.connect();
        this.wsCheckTimer = setTimeout(() => {
          if (WebSocketService.isConnected()) {
            this.websocketStatus = { text: 'Live Updates', color: 'success', icon: 'mdi-wifi' };
            this.setupDashboardListeners();
          } else {
            this.websocketStatus = { text: 'Offline Mode', color: 'error', icon: 'mdi-wifi-off' };
            this.startPollingUpdates();
          }
        }, 2000);
      } catch (e) {
        this.websocketStatus = { text: 'Offline Mode', color: 'error', icon: 'mdi-wifi-off' };
        this.startPollingUpdates();
      }
    },

    setupDashboardListeners() {
      const user = this.$store.getters['auth/user'];
      if (!user) return;
      const ch = WebSocketService.joinUserChannel(user.id);
      if (ch) {
        ch.listen('.DashboardUpdated', d => this.handleDashboardUpdate(d));
        ch.listen('.AppointmentCreated', e => this.handleAppointmentUpdate(e.data, 'created'));
        ch.listen('.AppointmentCancelled', e => this.handleAppointmentUpdate(e.data, 'cancelled'));
        ch.listen('.OrderCreated', e => this.handleOrderUpdate(e.data));
        ch.listen('.ConsultationCompleted', e => this.handleConsultationUpdate(e.data));
      }
    },

    handleDashboardUpdate(data) {
      if (data.type === 'stats' && data.stats) this.updateQuickStats(data.stats);
      if (data.type === 'revenue' && data.revenue) this.updateRevenueData(data.revenue);
    },

    handleAppointmentUpdate(data, type) {
      const map = { created: 'Scheduled', cancelled: 'Cancelled' };
      const idx = this.appointmentSummary.findIndex(a => a.label === map[type]);
      if (idx !== -1) this.appointmentSummary[idx].count = (parseInt(this.appointmentSummary[idx].count) + 1).toString();
      this.recentActivities.unshift({ text: type === 'created' ? 'New appointment scheduled' : 'Appointment cancelled', time: 'Just now', icon: type === 'created' ? 'mdi-calendar-plus' : 'mdi-calendar-remove', color: type === 'created' ? 'success' : 'warning' });
      if (this.recentActivities.length > 10) this.recentActivities.pop();
    },

    handleOrderUpdate() {
      const v = parseInt(this.quickStats[1].value) || 0;
      this.quickStats[1].value = (v + 1).toString();
      this.recentActivities.unshift({ text: 'New product order received', time: 'Just now', icon: 'mdi-shopping', color: 'primary' });
      if (this.recentActivities.length > 10) this.recentActivities.pop();
    },

    handleConsultationUpdate() {
      this.recentActivities.unshift({ text: 'Consultation completed', time: 'Just now', icon: 'mdi-check-circle', color: 'success' });
      if (this.recentActivities.length > 10) this.recentActivities.pop();
    },

    disconnectWebSocket() { try { WebSocketService.disconnect(); } catch (e) { /* ignore */ } },
    startPollingUpdates() {
      // Never stack timers: clear any existing one first (it is also cleared in beforeDestroy)
      this.stopPollingUpdates();
      this.pollTimer = setInterval(() => { this.fetchDashboardData(); }, 30000);
    },
    stopPollingUpdates() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },

    async refreshDashboard() {
      this.isLoading = true;
      try {
        await this.fetchDashboardData();
        await Promise.all([
          this.fetchOrderStats(this.dashboardDateFilter),
          this.fetchProductStats(this.dashboardDateFilter),
          this.fetchAppointmentStats(this.dashboardDateFilter),
        ]);
      } catch (e) { /* ignore */ }
      finally { this.isLoading = false; }
    }
  },
};
</script>
<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.dashboard-container { font-family: 'Inter', sans-serif; min-height: 100vh; padding: 24px 16px 12px; }

/* Hero header */
.header-content { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
.header-content.hero {
  padding: 16px 20px; border-radius: 14px; color: #fff;
  background: linear-gradient(120deg, #2f4a5c 0%, #4d7082 55%, #6b90a6 100%);
  box-shadow: 0 8px 24px rgba(77, 112, 130, 0.25);
  position: relative; overflow: hidden;
}
.header-content.hero::after {
  content: ""; position: absolute; right: -60px; top: -80px; width: 260px; height: 260px;
  border-radius: 50%; background: rgba(255, 255, 255, 0.07); pointer-events: none;
}
.header-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; position: relative; z-index: 1; }
.dashboard-title { font-size: 1.6rem; font-weight: 700; color: #fff; margin-bottom: 2px; letter-spacing: -0.01em; }
.dashboard-subtitle { font-size: 0.9rem; color: rgba(255, 255, 255, 0.8); margin-bottom: 0; }
.hero-filter { background: rgba(255, 255, 255, 0.95); border-radius: 10px; padding: 2px 6px; }

@media (max-width: 768px) {
  .header-content.hero { padding: 16px; }
  .header-content { flex-direction: column; align-items: flex-start; }
  .dashboard-title { font-size: 1.3rem; }
}

/* Stat Cards */
.stat-card {
  border-radius: 14px !important; cursor: pointer; border: 1px solid #e6ebef;
  border-left-width: 4px; transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.stat-card:hover { transform: translateY(-3px); }
.stat-green { border-left-color: #4CAF50; } .stat-blue { border-left-color: #2196F3; }
.stat-orange { border-left-color: #FF9800; } .stat-purple { border-left-color: #9C27B0; }
.stat-teal { border-left-color: #009688; } .stat-indigo { border-left-color: #3F51B5; }
.stat-cyan { border-left-color: #00BCD4; } .stat-red { border-left-color: #F44336; }

.stat-icon-wrap {
  width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.stat-icon-wrap .v-icon { font-size: 22px !important; transition: transform 0.25s ease; }
.stat-card:hover .stat-icon-wrap .v-icon { transform: scale(1.15) rotate(-6deg); }

.bg-green { background: #e8f5e9; } .bg-blue { background: #e3f2fd; }
.bg-orange { background: #fff3e0; } .bg-purple { background: #f3e5f5; }
.bg-teal { background: #e0f2f1; } .bg-indigo { background: #e8eaf6; }
.bg-cyan { background: #e0f7fa; } .bg-red { background: #ffebee; }

.stat-value { font-size: 1.5rem; font-weight: 700; color: #1f2d3d; line-height: 1.2; font-variant-numeric: tabular-nums; }
.stat-label { font-size: 0.75rem; color: #637381; font-weight: 500; }

/* Dashboard Cards */
.dash-card { border-radius: 14px !important; border: 1px solid #e6ebef; }
.dash-card-title { font-size: 1rem !important; font-weight: 600; padding: 16px 18px 10px !important; }
.dash-card-title > .v-icon:first-child {
  width: 30px; height: 30px; border-radius: 8px; background: #eef3f5; margin-right: 10px !important; font-size: 16px !important;
}
.dash-card > .v-card__text { padding: 4px 16px 16px !important; }

.toggle-sm .v-btn { font-size: 0.65rem !important; height: 24px !important; min-width: 50px !important; }

/* Overview Cards (Orders & Appointments) */
.overview-cards-row {
  display: flex; gap: 10px; flex-wrap: wrap;
}
.overview-card {
  flex: 1 1 0; min-width: 80px;
  background: #fff; border: 1px solid #e6ebef; border-radius: 12px;
  padding: 12px 8px; text-align: center; transition: all 0.2s;
}
.overview-card:hover { background: #f3f7fa; border-color: #c3d3de; transform: translateY(-1px); }
.overview-card-dot {
  width: 10px; height: 10px; border-radius: 50%; margin: 0 auto 6px;
}
.dot-blue { background: #2196F3; } .dot-green { background: #4CAF50; }
.dot-red { background: #F44336; } .dot-orange { background: #FF9800; }
.overview-card-count { font-size: 1.5rem; font-weight: 700; color: #2c3e50; line-height: 1; margin-bottom: 2px; }
.overview-card-label { font-size: 0.7rem; color: #7f8c8d; font-weight: 500; }

/* Centered Chart */
.chart-centered { max-width: 280px; margin: 0 auto; }

@media (max-width: 600px) {
  .overview-cards-row { gap: 6px; }
  .overview-card { min-width: 60px; padding: 8px 4px; }
  .overview-card-count { font-size: 1.2rem; }
  .chart-centered { max-width: 200px; }
}

/* Top Items */
.top-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 0; border-bottom: 1px solid #f0f0f0;
}
.top-item:last-child { border-bottom: none; }
.top-rank {
  width: 22px; height: 22px; border-radius: 50%; background: #e8eaf6;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 700; color: #3F51B5; flex-shrink: 0;
}
.top-name { font-size: 0.85rem; font-weight: 500; color: #2c3e50; flex-grow: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.top-meta { font-size: 0.7rem; color: #999; white-space: nowrap; }

/* Quick Action Buttons */
.action-btn { border-radius: 8px !important; text-transform: none; font-weight: 500; letter-spacing: 0; }

/* Approval Items */
.approval-item {
  display: flex; align-items: center; gap: 4px;
  padding: 8px 0; border-bottom: 1px solid #f5f5f5; cursor: pointer;
}
.approval-item:last-child { border-bottom: none; }
.approval-item:hover { background: #fafafa; }

/* Activity List */
.activity-list { display: flex; flex-direction: column; gap: 2px; }
.activity-row {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 8px; border-radius: 8px; border-left: 3px solid transparent;
}
.activity-row:hover { background: #f8f9fa; }
.activity-icon { margin-top: 2px; flex-shrink: 0; }
.activity-info { flex-grow: 1; min-width: 0; }

/* Responsive */
@media (max-width: 600px) {
  .dashboard-container { padding: 8px 12px; }
  .stat-value { font-size: 1.1rem; }
  .stat-icon-wrap { width: 36px; height: 36px; }
  .order-grid { grid-template-columns: 1fr; }
}

/* Dark theme */
.theme--dark .dashboard-container { background: #121212; }
.theme--dark .dashboard-title { color: #fff; }
.theme--dark .stat-value, .theme--dark .order-count, .theme--dark .appt-count,
.theme--dark .top-name { color: #fff; }
.theme--dark .order-grid-item, .theme--dark .appt-row,
.theme--dark .activity-row:hover { background: #1e1e1e; border-color: #333; }
</style>
